import { flow, getEnv, getParent, types } from 'mobx-state-tree';

import { GET_POLICY_EQUITY_STATUS } from 'customer/components/policy/policy.queries';

const PolicyEquityStatus = types
  .model({
    blendedPremium: types.maybe(types.number),
    policyDays: types.maybe(types.number),
    status: types.optional(types.enumeration(['loading', 'done', 'error']), 'loading'),
    premiumBilled: types.maybe(types.number),
    premiumReceived: types.maybe(types.number)
  })
  .views((self) => ({
    get policy() {
      return getParent(self);
    },

    get totalBilled() {
      const { surplusContribution, totalFeeCost } = self.policy;

      return self.premiumBilled + surplusContribution + totalFeeCost;
    },
    get premium() {
      const { premium } = self.policy;

      return premium;
    },
    get totalFeeCost() {
      const { totalFeeCost } = self.policy;
      return totalFeeCost;
    },
    get surplusContribution() {
      const { surplusContribution } = self.policy;

      return surplusContribution;
    },
    get totalPrice() {
      const { surplusContribution, totalFeeCost, premium } = self.policy;

      return premium + totalFeeCost + surplusContribution;
    },

    get totalReceived() {
      return self.policy.totalReceived;
    },

    get feesBilled() {
      return self.policy.totalFeeCost;
    },

    get writeOffTotal() {
      return self.policy.writeOffTotal;
    },

    get feesPaidInFull() {
      const { feesBilled, remainingAfterSurplus, surplusPaidInFull } = self;

      return surplusPaidInFull && remainingAfterSurplus >= feesBilled;
    },

    get premiumPaidInFull() {
      const { feesPaidInFull, premium, remainingAfterFees } = self;

      return feesPaidInFull && remainingAfterFees >= premium;
    },

    get surplusBilled() {
      return self.policy.surplusContribution;
    },

    get surplusPaidInFull() {
      const { surplusBilled, totalReceived } = self;

      return totalReceived >= surplusBilled;
    },

    get remainingAfterSurplus() {
      const { surplusContribution, surplusPaidInFull, totalReceived } = self;

      return surplusPaidInFull ? totalReceived - surplusContribution : 0;
    },

    get remainingAfterFees() {
      const { totalFeeCost, feesPaidInFull, remainingAfterSurplus } = self;

      return feesPaidInFull ? remainingAfterSurplus - totalFeeCost : 0;
    },

    get billedCostBreakdown() {
      const { feesBilled: fees, premiumBilled: premium, surplusBilled: surplus, totalBilled: total } = self;

      return {
        surplus,
        fees,
        premium,
        total
      };
    },

    get remainingCostBreakdown() {
      const {
        totalFeeCost,
        feesPaidInFull,
        premium,
        premiumPaidInFull,
        remainingAfterSurplus,
        remainingAfterFees,
        surplusContribution,
        surplusPaidInFull,
        totalReceived,
        totalPrice
      } = self;

      return {
        surplus: surplusPaidInFull ? 0 : surplusContribution - totalReceived,
        fees: feesPaidInFull ? 0 : totalFeeCost - remainingAfterSurplus,
        premium: premiumPaidInFull ? 0 : premium - remainingAfterFees,
        total: totalReceived >= totalPrice ? 0 : totalPrice - totalReceived
      };
    },

    get paidCostBreakdown() {
      const {
        feesBilled,
        feesPaidInFull,
        premiumBilled,
        premiumPaidInFull,
        remainingAfterFees,
        remainingAfterSurplus,
        surplusBilled,
        surplusPaidInFull,
        totalReceived,
        writeOffTotal
      } = self;

      return {
        surplus: surplusPaidInFull ? surplusBilled : totalReceived,
        fees: feesPaidInFull ? feesBilled : remainingAfterSurplus,
        premium: premiumPaidInFull ? premiumBilled + writeOffTotal : remainingAfterFees,
        total: totalReceived
      };
    },

    get blendedDailyRate() {
      return self.blendedPremium / self.policyDays;
    }
  }))
  .actions((self) => ({
    fetchPolicyEquityStatus: flow(function* fetchPolicyEquityStatus() {
      const { client } = getEnv(self);

      const policyId = yield self.policy.policyId;

      try {
        const {
          data: { getPolicyEquityStatus }
        } = yield client.query({
          query: GET_POLICY_EQUITY_STATUS,
          fetchPolicy: 'network-only',
          variables: { policyId }
        });

        const { blendedPremium, policyDays, totalPremiumBilled, totalPremiumReceived } = getPolicyEquityStatus;

        self.blendedPremium = blendedPremium;
        self.policyDays = policyDays;
        self.premiumBilled = totalPremiumBilled;
        self.premiumReceived = totalPremiumReceived;

        self.status = 'done';
      } catch (e) {
        self.status = 'error';
        throw e;
      }
    }),

    afterAttach() {
      self.fetchPolicyEquityStatus();
    }
  }));

export default PolicyEquityStatus;
